import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getPersonalActivity, type PersonalActivityDay } from '../../../shared/lib/api/activity'
import { toLocalDateString } from '../../../shared/lib/formatters'

interface DayData {
  date: Date
  count: number
  isToday: boolean
  tasks: number
  habits: number
  deep_work_sessions: number
  deep_work_minutes: number
}

interface TooltipState {
  day: DayData | null
  x: number
  y: number
  flipped: boolean
}

export function YearActivityGraph() {
  const [dayData, setDayData] = useState<DayData[]>([])
  const [loading, setLoading] = useState(true)
  const [tooltip, setTooltip] = useState<TooltipState>({ day: null, x: 0, y: 0, flipped: false })
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    async function fetchActivity() {
      try {
        const currentYear = new Date().getFullYear()
        const since = `${currentYear}-01-01`
        const personalActivity = await getPersonalActivity(since)
        const grid = generateYearGrid(personalActivity)
        setDayData(grid)
      } catch (error) {
        console.error('Failed to fetch activity:', error)
        // Generate empty grid on error
        setDayData(generateYearGrid([]))
      } finally {
        setLoading(false)
      }
    }
    fetchActivity()
  }, [])

  if (loading) {
    return (
      <div className="hidden sm:block w-[560px] h-[80px] bg-white/[0.02] rounded-md animate-pulse" />
    )
  }

  // Group days into weeks (7 days each)
  const weeks: DayData[][] = []
  for (let i = 0; i < dayData.length; i += 7) {
    weeks.push(dayData.slice(i, i + 7))
  }

  const handleMouseEnter = (day: DayData, event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const containerRect = containerRef.current?.getBoundingClientRect()
    if (containerRect) {
      setTooltip({
        day,
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.bottom - containerRect.top,
        flipped: true,
      })
    }
  }

  const handleMouseLeave = () => {
    setTooltip({ day: null, x: 0, y: 0, flipped: false })
  }

  const handleClick = (day: DayData) => {
    const dateStr = toLocalDateString(day.date)
    navigate(`/day/${dateStr}`)
  }

  return (
    <div className="hidden sm:block relative" ref={containerRef}>
      <div className="flex gap-[2px]">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="flex flex-col gap-[2px]">
            {week.map((day, dayIndex) => {
              const intensity = getIntensityClass(day.count)
              return (
                <div
                  key={dayIndex}
                  className={`w-[10px] h-[10px] rounded-[2px] cursor-pointer ${intensity} ${
                    day.isToday ? 'ring-1 ring-white/30' : ''
                  }`}
                  onMouseEnter={(e) => handleMouseEnter(day, e)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleClick(day)}
                />
              )
            })}
          </div>
        ))}
      </div>
      {tooltip.day && (
        <div
          className="absolute z-50 bg-gray-900 border border-white/10 rounded-md px-3 py-2 text-xs text-white/90 pointer-events-none"
          style={{
            left: `${tooltip.x}px`,
            top: tooltip.flipped ? `${tooltip.y + 6}px` : `${tooltip.y - 6}px`,
            transform: `translate(-50%, ${tooltip.flipped ? '0%' : '-100%'})`
          }}
        >
          <div className="font-semibold mb-1">
            {tooltip.day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          {tooltip.day.count === 0 ? (
            <div className="text-white/60">No activity</div>
          ) : (
            <div className="space-y-0.5 text-white/70">
              {tooltip.day.tasks > 0 && <div>{tooltip.day.tasks} task{tooltip.day.tasks !== 1 ? 's' : ''}</div>}
              {tooltip.day.habits > 0 && <div>{tooltip.day.habits} habit{tooltip.day.habits !== 1 ? 's' : ''}</div>}
              {tooltip.day.deep_work_sessions > 0 && <div>{tooltip.day.deep_work_sessions} deep work session{tooltip.day.deep_work_sessions !== 1 ? 's' : ''}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function generateYearGrid(personalActivity: PersonalActivityDay[]): DayData[] {
  const now = new Date()
  const currentYear = now.getFullYear()
  const todayStr = toLocalDateString(now)

  // Create a map for fast lookup
  const activityMap = new Map<string, PersonalActivityDay>()
  for (const day of personalActivity) {
    activityMap.set(day.date, day)
  }

  // Start from January 1st of current year
  const yearStart = new Date(currentYear, 0, 1)

  // Find what day of week Jan 1 falls on (0 = Sunday, 1 = Monday, etc.)
  const startDayOfWeek = yearStart.getDay()

  // Adjust to start on Monday (GitHub style)
  // If Jan 1 is Sunday (0), we need to go back 6 days to previous Monday
  // If Jan 1 is Monday (1), we start there (0 days back)
  // If Jan 1 is Tuesday (2), we go back 1 day to Monday
  const daysToMonday = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1

  const gridStart = new Date(yearStart)
  gridStart.setDate(gridStart.getDate() - daysToMonday)

  // Generate 53 weeks * 7 days = 371 days to cover full year
  const days: DayData[] = []
  const endOfYear = new Date(currentYear, 11, 31)

  for (let i = 0; i < 371; i++) {
    const date = new Date(gridStart)
    date.setDate(date.getDate() + i)

    // Stop if we've passed the end of the year
    if (date > endOfYear) break

    const dateStr = toLocalDateString(date)
    const activity = activityMap.get(dateStr)
    const isToday = dateStr === todayStr

    days.push({
      date,
      count: activity?.count || 0,
      isToday,
      tasks: activity?.tasks || 0,
      habits: activity?.habits || 0,
      deep_work_sessions: activity?.deep_work_sessions || 0,
      deep_work_minutes: activity?.deep_work_minutes || 0
    })
  }

  return days
}

function getIntensityClass(count: number): string {
  if (count === 0) return 'bg-white/[0.04]'
  if (count === 1) return 'bg-emerald-900/80'
  if (count <= 3) return 'bg-emerald-700/80'
  if (count <= 6) return 'bg-emerald-500/70'
  return 'bg-emerald-400/90'
}
